
import { useAuth } from '@/hooks/useAuth';
import { Family, Parent, Student, BillingStatus, InviteStatus } from '@/types/models';
import { toast } from 'sonner';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { FamilyService } from '@/services/familyService';

export const useFamilyMutations = () => {
  const { user } = useAuth();
  const { handleError, wrapAsync } = useErrorHandler('useFamilyMutations');

  const handleFamilyAdded = async (newFamily: Omit<Family, 'id' | 'created_at'>, setFamilies: React.Dispatch<React.SetStateAction<Family[]>>) => {
    if (!user?.user_metadata?.organization_id) {
      toast.error("No organization ID available");
      return false;
    }

    try {
      const familyWithOrgId = {
        ...newFamily,
        organization_id: user.user_metadata.organization_id
      };

      // Make sure billing_status is typed correctly as BillingStatus
      const typedFamily: Omit<Family, 'id' | 'created_at'> = {
        ...familyWithOrgId,
        billing_status: familyWithOrgId.billing_status as BillingStatus
      };

      // Validate the data before sending it to the API
      if (!typedFamily.name) {
        toast.error("Family name is required");
        return false;
      }

      try {
        const family = await FamilyService.createFamily(typedFamily);
        
        if (family) {
          setFamilies(prev => [...prev, family]);
          toast.success("Family added successfully");
          return true;
        }
        return false;
      } catch (error) {
        // Even if there's an error, check if the family was actually created
        // by fetching families after a short delay
        setTimeout(async () => {
          try {
            const updatedFamilies = await FamilyService.getFamilies(user.user_metadata.organization_id);
            setFamilies(updatedFamilies);
          } catch (e) {
            // Silently fail if we can't refresh the families list
          }
        }, 500);
        
        handleError(error, "adding family", { additionalInfo: { familyData: typedFamily } }, true);
        return false;
      }
    } catch (error) {
      handleError(error, "adding family", { additionalInfo: { familyData: newFamily } }, true);
      return false;
    }
  };

  const handleFamilyWithParentAdded = async (
    newFamily: Family, 
    newParent: Parent,
    setFamilies: React.Dispatch<React.SetStateAction<Family[]>>,
    setAllParents: React.Dispatch<React.SetStateAction<Parent[]>>
  ) => {
    const organizationId = user?.user_metadata?.organization_id;

    if (!organizationId) {
      toast.error('No organization ID available');
      return false;
    }

    try {
      console.log("Creating family with parent:", newFamily, newParent);
      
      // Use the service to create both in a single operation
      const result = await FamilyService.createFamilyWithParent(
        {
          name: newFamily.name,
          organization_id: organizationId,
          billing_status: newFamily.billing_status || 'current'
        },
        {
          ...newParent,
          organization_id: organizationId,
          invite_status: newParent.invite_status || 'pending',
          is_primary: newParent.is_primary || false
        }
      );

      // Update state with the returned data
      if (result && result.family && result.parent) {
        setFamilies(prev => [...prev, result.family]);
        setAllParents(prev => [...prev, result.parent]);
        toast.success('Family with parent added successfully');
        return true;
      }
      
      return false;
    } catch (error) {
      handleError(error, 'adding family with parent', {
        additionalInfo: {
          familyData: newFamily,
          parentData: newParent
        }
      }, true);
      return false;
    }
  };

  const handleStudentAdded = async (newStudent: Student, setAllStudents: React.Dispatch<React.SetStateAction<Student[]>>) => {
    try {
      // Convert Student object to regular Record for Supabase
      const studentRecord: Omit<Student, 'id' | 'created_at'> = {
        family_id: newStudent.family_id,
        organization_id: newStudent.organization_id,
        first_name: newStudent.first_name,
        last_name: newStudent.last_name,
        date_of_birth: newStudent.date_of_birth,
        gender: newStudent.gender,
        notes: newStudent.notes
      };

      const [student, error] = await wrapAsync(
        FamilyService.addStudent(studentRecord),
        'adding student',
        { additionalInfo: { studentData: studentRecord } },
        true
      );

      if (error || !student) return false;

      setAllStudents(prev => [...prev, student]);
      
      return true;
    } catch (error) {
      handleError(error, "adding student", { additionalInfo: { studentData: newStudent } }, true);
      return false;
    }
  };

  return {
    handleFamilyAdded,
    handleFamilyWithParentAdded,
    handleStudentAdded
  };
};
